import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Info,
  Cloud,
  Link as LinkIcon,
} from "lucide-react";
import { SiJira, SiGithub, SiSlack, SiLinear } from "react-icons/si";
import { MdEmail, MdPhoneIphone } from "react-icons/md";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { IntegrationIcon } from "@/components/integrations/IntegrationIcon";
import { IntegrationCardSkeleton } from "@/components/integrations/IntegrationCardSkeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Integrations: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState<
    string | null
  >(null);
  const [integrations, setIntegrations] = useState([
    {
      id: "jira",
      name: "Jira",
      description: "Project management and issue tracking",
      status: "disconnected" as "connected" | "disconnected" | "connecting",
      enabled: false,
      icon: SiJira,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      accountName: null as string | null,
      accountUrl: null as string | null,
    },
    {
      id: "github",
      name: "GitHub",
      description: "Code repositories and pull requests",
      status: "disconnected",
      enabled: false,
      icon: SiGithub,
      color: "text-gray-600",
      bgColor: "bg-gray-100",
    },
    {
      id: "slack",
      name: "Slack",
      description: "Team communication and notifications",
      status: "disconnected",
      enabled: false,
      icon: SiSlack,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      id: "azure-devops",
      name: "Azure DevOps",
      description: "Microsoft development platform",
      status: "disconnected",
      enabled: false,
      icon: Cloud,
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
    {
      id: "linear",
      name: "Linear",
      description: "Modern issue tracking",
      status: "disconnected",
      enabled: false,
      icon: SiLinear,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
    {
      id: "email",
      name: "Email",
      description: "Email notifications",
      status: "disconnected",
      enabled: false,
      icon: MdEmail,
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
    {
      id: "mobile-app",
      name: "Mobile App",
      description: "Push notifications",
      status: "disconnected",
      enabled: false,
      icon: MdPhoneIphone,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
  ]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "connecting":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "disconnected":
        return <XCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "connected":
        return "Connected";
      case "connecting":
        return "Not Connected";
      case "disconnected":
        return "Not Connected";
      default:
        return "Not Connected";
    }
  };

  // Check connection status on mount
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const checkConnections = async () => {
      // Check Jira connection
      if (!supabase) {
        setIsLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("integration_tokens")
          .select("id, metadata")
          .eq("user_id", user.id)
          .eq("provider", "jira")
          .maybeSingle(); // Use maybeSingle() to handle "no rows" case gracefully

        if (!error && data) {
          const metadata = data.metadata as {
            name?: string;
            url?: string;
          } | null;
          setIntegrations((prev) =>
            prev.map((int) =>
              int.id === "jira"
                ? {
                    ...int,
                    status: "connected" as const,
                    enabled: true,
                    accountName: metadata?.name || null,
                    accountUrl: metadata?.url || null,
                  }
                : int,
            ),
          );
        }
      } catch (error) {
        console.error("Error checking Jira connection:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkConnections();
  }, [user]);

  // Check for success redirect and show toast notification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get("connected");
    const provider = urlParams.get("provider");

    if (connected === "true" && provider && user && supabase) {
      // Re-check connections to ensure we have the latest data
      const supabaseClient = supabase; // Store in const for type narrowing
      const checkAndShowToast = async () => {
        try {
          const { data } = await supabaseClient
            .from("integration_tokens")
            .select("metadata")
            .eq("user_id", user.id)
            .eq("provider", provider)
            .maybeSingle();

          if (data) {
            const metadata = data.metadata as {
              name?: string;
              url?: string;
            } | null;
            const accountInfo = metadata?.name
              ? `Connected to ${metadata.name}`
              : "Integration connected successfully";

            toast.success(accountInfo, {
              description: metadata?.url
                ? `Jira site: ${metadata.url}`
                : undefined,
            });
          } else {
            toast.success("Integration connected successfully");
          }
        } catch (error) {
          console.error("Error fetching integration info:", error);
          toast.success("Integration connected successfully");
        }

        // Clean up URL
        window.history.replaceState({}, "", "/integrations");
      };

      checkAndShowToast();
    }
  }, [user]);

  const handleDisconnectClick = (provider: string) => {
    if (!user) {
      toast.error("Please log in to disconnect integrations");
      return;
    }
    setIntegrationToDisconnect(provider);
    setDisconnectDialogOpen(true);
  };

  const handleDisconnectConfirm = async () => {
    if (!integrationToDisconnect || !user) {
      return;
    }

    const provider = integrationToDisconnect;
    setDisconnectDialogOpen(false);

    setIntegrations((prev) =>
      prev.map((int) =>
        int.id === provider
          ? {
              ...int,
              status: "disconnected" as const,
              accountName: null,
              accountUrl: null,
            }
          : int,
      ),
    );

    if (!supabase) {
      toast.error("Unable to disconnect. Please try again.");
      return;
    }

    try {
      if (provider === "jira") {
        const { error } = await supabase
          .from("integration_tokens")
          .delete()
          .eq("user_id", user.id)
          .eq("provider", "jira");

        if (error) {
          throw error;
        }

        setIntegrations((prev) =>
          prev.map((int) =>
            int.id === provider
              ? {
                  ...int,
                  status: "disconnected" as const,
                  enabled: false,
                  accountName: null,
                  accountUrl: null,
                }
              : int,
          ),
        );
        toast.success("Integration disconnected successfully");
      } else {
        toast.info(`${provider} disconnect is not yet implemented`);
        setIntegrations((prev) =>
          prev.map((int) =>
            int.id === provider
              ? { ...int, status: "disconnected" as const }
              : int,
          ),
        );
      }
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      toast.error(`Failed to disconnect ${provider}. Please try again.`);
      // Re-check connection status
      const { data } = await supabase
        .from("integration_tokens")
        .select("id")
        .eq("user_id", user.id)
        .eq("provider", provider)
        .maybeSingle();

      if (data) {
        setIntegrations((prev) =>
          prev.map((int) =>
            int.id === provider
              ? { ...int, status: "connected" as const, enabled: true }
              : int,
          ),
        );
      }
    } finally {
      setIntegrationToDisconnect(null);
    }
  };

  const handleConnect = async (provider: string) => {
    if (!user) {
      toast.error("Please log in to connect integrations");
      return;
    }

    // Update status to connecting
    setIntegrations((prev) =>
      prev.map((int) =>
        int.id === provider ? { ...int, status: "connecting" as const } : int,
      ),
    );

    try {
      if (provider === "jira") {
        // Get Supabase URL and construct OAuth URL
        // Using oauth-jira-redirect for compatibility with existing Atlassian app configuration
        // Convert regular Supabase URL to functions domain for redirect URI
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!supabaseUrl) {
          throw new Error("VITE_SUPABASE_URL is not configured");
        }

        // Convert https://xxx.supabase.co to https://xxx.functions.supabase.co
        // Handle both regular and functions domains
        let functionsUrl: string;
        if (supabaseUrl.includes(".functions.supabase.co")) {
          // Already a functions URL
          functionsUrl = supabaseUrl.replace(/\/$/, ""); // Remove trailing slash if present
        } else {
          // Convert from regular domain to functions domain
          functionsUrl = supabaseUrl.replace(
            /^https:\/\/([^.]+)\.supabase\.co$/,
            "https://$1.functions.supabase.co",
          );
        }

        // Ensure redirect URI matches exactly what's configured in Atlassian app
        // Expected: https://cynzxhgcxhbzspnapczm.functions.supabase.co/functions/v1/oauth-jira-redirect
        const redirectUri = `${functionsUrl}/functions/v1/oauth-jira-redirect`;

        // Log for debugging
        console.log("Jira OAuth Configuration:", {
          supabaseUrl,
          functionsUrl,
          redirectUri,
          clientId: import.meta.env.VITE_JIRA_CLIENT_ID
            ? "***configured***"
            : "MISSING",
        });

        // Encode state with user ID and frontend URL for redirect
        const frontendUrl = window.location.origin;
        const state = JSON.stringify({ userId: user.id, frontendUrl });

        // Construct Jira OAuth URL
        // Trim whitespace/newlines from client ID (common issue with env vars)
        const clientId = import.meta.env.VITE_JIRA_CLIENT_ID?.trim();
        if (!clientId) {
          throw new Error("Jira OAuth client ID not configured");
        }

        const authUrl = new URL("https://auth.atlassian.com/authorize");
        authUrl.searchParams.set("audience", "api.atlassian.com");
        authUrl.searchParams.set("client_id", clientId);
        // Using Classic Jira platform REST API scopes for full functionality
        authUrl.searchParams.set(
          "scope",
          "read:jira-work manage:jira-project manage:jira-configuration read:jira-user write:jira-work",
        );
        authUrl.searchParams.set("redirect_uri", redirectUri);
        authUrl.searchParams.set("state", state);
        authUrl.searchParams.set("response_type", "code");
        authUrl.searchParams.set("prompt", "consent");

        // Redirect to Jira OAuth
        window.location.href = authUrl.toString();
      } else {
        toast.info(`${provider} integration is not yet implemented`);
        setIntegrations((prev) =>
          prev.map((int) =>
            int.id === provider
              ? { ...int, status: "disconnected" as const }
              : int,
          ),
        );
      }
    } catch (error) {
      console.error(`Error connecting to ${provider}:`, error);
      toast.error(`Failed to connect to ${provider}. Please try again.`);
      setIntegrations((prev) =>
        prev.map((int) =>
          int.id === provider
            ? { ...int, status: "disconnected" as const }
            : int,
        ),
      );
    }
  };

  // Separate available and coming soon integrations
  const availableIntegrations = integrations.filter((int) => int.id === "jira");
  const comingSoonIntegrations = integrations.filter(
    (int) => int.id !== "jira",
  );

  return (
    <TooltipProvider>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground mt-2">
            Connect your favorite development tools to streamline your workflow
          </p>
        </div>

        {/* Info Alert - Only show if no integrations connected */}
        {integrations.every((int) => int.status === "disconnected") && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Connect your tools to enable AI-powered project management. Start
              by connecting Jira to search issues, create tasks, and manage your
              projects through natural language.
            </AlertDescription>
          </Alert>
        )}

        {/* Available Integrations Section */}
        {availableIntegrations.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Available Integrations
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Connect these tools to get started
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {isLoading
                ? Array.from({ length: availableIntegrations.length }).map(
                    (_, index) => <IntegrationCardSkeleton key={index} />,
                  )
                : availableIntegrations.map((integration) => (
                    <Card
                      key={integration.id}
                      className="relative transition-shadow hover:shadow-md"
                    >
                      <CardHeader className="pb-3 px-4 pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-2.5 flex-1">
                            <IntegrationIcon
                              icon={integration.icon}
                              color={integration.color}
                              bgColor={integration.bgColor}
                              available={integration.id === "jira"}
                              size="h-5 w-5"
                            />
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base">
                                {integration.name}
                              </CardTitle>
                              <CardDescription className="text-xs mt-0.5">
                                {integration.description}
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1.5 ml-3">
                            {getStatusIcon(integration.status)}
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {getStatusText(integration.status)}
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 px-4 pb-4 space-y-3">
                        {integration.status === "connected" &&
                        integration.accountName ? (
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200 text-xs px-2 py-0.5"
                              >
                                <CheckCircle className="h-2.5 w-2.5 mr-1" />
                                {integration.accountName}
                              </Badge>
                              {integration.accountUrl && (
                                <a
                                  href={integration.accountUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                                >
                                  <LinkIcon className="h-2.5 w-2.5" />
                                  <span className="truncate max-w-[150px]">
                                    {integration.accountUrl.replace(
                                      /^https?:\/\//,
                                      "",
                                    )}
                                  </span>
                                </a>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDisconnectClick(integration.id)
                              }
                              className="shrink-0 h-8 text-xs px-3"
                            >
                              <XCircle className="h-3 w-3 mr-1.5" />
                              Disconnect
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() =>
                                      handleConnect(integration.id)
                                    }
                                    disabled={
                                      integration.status === "connecting" ||
                                      (integration.id !== "jira" &&
                                        integration.status !== "connected")
                                    }
                                    className="w-full sm:w-auto h-8 text-xs px-3"
                                  >
                                    {integration.status === "connecting" ? (
                                      <>
                                        <Clock className="h-3 w-3 mr-1.5 animate-spin" />
                                        Connecting...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-3 w-3 mr-1.5" />
                                        Connect
                                      </>
                                    )}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {integration.id !== "jira" && (
                                <TooltipContent>
                                  <p>This integration is coming soon</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
            </div>
          </div>
        )}

        {/* Coming Soon Integrations Section */}
        {comingSoonIntegrations.length > 0 && (
          <div className="space-y-4">
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold tracking-tight">
                Coming Soon
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                These integrations will be available in the future
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {comingSoonIntegrations.map((integration) => (
                <Card
                  key={integration.id}
                  className="relative opacity-75 transition-shadow hover:shadow-md"
                >
                  <CardHeader className="pb-3 px-4 pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2.5 flex-1">
                        <IntegrationIcon
                          icon={integration.icon}
                          color={integration.color}
                          bgColor={integration.bgColor}
                          available={false}
                          size="h-5 w-5"
                        />
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base">
                            {integration.name}
                          </CardTitle>
                          <CardDescription className="text-xs mt-0.5">
                            {integration.description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1.5 ml-3">
                        {getStatusIcon(integration.status)}
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {getStatusText(integration.status)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="flex items-center justify-between">
                      <Badge
                        variant="secondary"
                        className="text-xs px-2 py-0.5"
                      >
                        Coming Soon
                      </Badge>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled
                              className="w-full sm:w-auto h-8 text-xs px-3"
                            >
                              <ExternalLink className="h-3 w-3 mr-1.5" />
                              Connect
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>This integration is coming soon</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Disconnect Confirmation Dialog */}
        <AlertDialog
          open={disconnectDialogOpen}
          onOpenChange={setDisconnectDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect Integration</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to disconnect{" "}
                {integrationToDisconnect
                  ? integrations.find(
                      (int) => int.id === integrationToDisconnect,
                    )?.name
                  : "this integration"}
                ? You will need to reconnect to use this integration again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDisconnectConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Disconnect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};
